/**
 * SaveSessionToLibrary.jsx
 *
 * Post-session CTA — appears on the reflection screen after the user
 * picks a feeling. Two states:
 *
 *   1. Not saved yet           → "Save to my library" button
 *   2. Saved (`entry` is set)  → "Play from library" + (premium only)
 *                                "Download MP3"
 *
 * Why this matters: the live session is regenerated each time the user
 * starts it (ElevenLabs TTS hits the API, etc.). By persisting the
 * fully-composited MP3 to disk + Mongo `session_library`, the user can
 * replay the EXACT same session later — same affirmations, same
 * subliminal mix, same music bed — without burning more API credits.
 * Premium members additionally get a real downloadable file they can
 * load into Spotify / Apple Music / wherever for offline use.
 */
import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Check, Download, Play, Bookmark, Lock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");

export const SaveSessionToLibrary = ({ sessionId, isPremium = false }) => {
  const [saving, setSaving] = useState(false);
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState(null);

  if (!sessionId) return null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/private-suite/sessions/${sessionId}/save-to-library`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setEntry(res.data);
    } catch (e) {
      console.error("[SaveSessionToLibrary] save failed", e);
      setError("Couldn't save right now — try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!entry || !isPremium) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/private-suite/library/${entry.id}/download`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          responseType: "blob",
        },
      );
      const blob = new Blob([res.data], { type: "audio/mpeg" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(entry.name || "reclaim_session").replace(/[^a-z0-9 ._-]/gi, "")}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[SaveSessionToLibrary] download failed", e);
      setError("Download failed — try again.");
    }
  };

  // Initial state
  if (!entry) {
    return (
      <div className="w-full flex flex-col items-center gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full max-w-xs flex items-center justify-center gap-2 px-5 py-3 rounded-full transition-all"
          style={{
            background: "rgba(215, 187, 122, 0.16)",
            border: "1px solid rgba(215, 187, 122, 0.55)",
            color: "#F3EDDD",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.72rem",
            letterSpacing: "0.24em",
            textTransform: "uppercase",
          }}
          data-testid="save-session-to-library-btn"
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Bookmark size={14} />
          )}
          {saving ? "Saving" : "Save to my library"}
        </button>
        {error && (
          <p className="text-rose-300/70 text-xs" data-testid="save-session-error">{error}</p>
        )}
      </div>
    );
  }

  // Saved state — show play + download (premium only)
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full flex flex-col items-center gap-3"
    >
      <div className="flex items-center gap-2 text-amber-100/80" data-testid="save-session-saved">
        <Check size={14} className="text-emerald-300" />
        <span className="text-xs uppercase tracking-[0.24em]">Saved to your library</span>
      </div>
      <p
        className="italic"
        style={{
          color: "rgba(243, 237, 221, 0.78)",
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1.02rem",
        }}
      >
        {entry.name}
      </p>
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <a
          href={`${API_URL}${entry.audio_url}?token=${encodeURIComponent(getToken() || "")}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all"
          style={{
            background: "rgba(255, 255, 255, 0.06)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            color: "#F3EDDD",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.66rem",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
          }}
          data-testid="library-play-link"
        >
          <Play size={12} />
          Play
        </a>
        {isPremium ? (
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all"
            style={{
              background: "rgba(215, 187, 122, 0.22)",
              border: "1px solid rgba(215, 187, 122, 0.7)",
              color: "#F3EDDD",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.66rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
            data-testid="library-download-btn"
          >
            <Download size={12} />
            Download MP3
          </button>
        ) : (
          <span
            className="flex items-center gap-2 px-4 py-2.5 rounded-full"
            style={{
              background: "rgba(15, 27, 23, 0.4)",
              border: "1px dashed rgba(215, 187, 122, 0.35)",
              color: "rgba(243, 237, 221, 0.55)",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.62rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
            data-testid="library-download-premium-lock"
          >
            <Lock size={11} />
            Download · Premium
          </span>
        )}
      </div>
      {error && (
        <p className="text-rose-300/70 text-xs" data-testid="save-session-error">{error}</p>
      )}
    </motion.div>
  );
};

export default SaveSessionToLibrary;
